import { Flex, Radio, Space, Tag, Input, Button, Row, Form } from "antd"
import { AppModalConfig } from "src/global/ui/AppModal/AppModal"
import { BodyNormal, Heading3 } from "src/global/ui/typography/Text/Text"
import { useEffect } from "react"
import { AnalyticsEvent, useAnalytics } from "src/global/hooks/analytics/useAnalytics"
import { useTheme } from "styled-components"
import { Icon } from "src/global/ui/Icon"

type SurveyConfigInput = {
  title: string
  eventName: AnalyticsEvent
  surveyId: string
  surveyName: string
}

export const EXPECT_AI_SURVEY_TEXT = {
  title: "Recommended Expectations",
  eventName: "survey.seen",
  surveyId: "5e21ca48-3553-42ef-844f-77552d929665",
  surveyName: "expect_ai_approval_survey",
} satisfies SurveyConfigInput

const createModalConfig = (input: SurveyConfigInput): ((handleSubmit: () => void) => typeof AppModalConfig) => {
  return (handleSubmit: () => void) => ({
    ...AppModalConfig,
    width: 394,
    okButtonProps: { style: { display: "none" } },
    title: (
      <Title title={input.title} eventName={input.eventName} surveyId={input.surveyId} surveyName={input.surveyName} />
    ),
    content: <Content onSubmit={handleSubmit} />,
  })
}

export const ExpectAiSurveyModalConfig = createModalConfig(EXPECT_AI_SURVEY_TEXT)

function Title({
  title,
  eventName,
  surveyId,
  surveyName,
}: Pick<SurveyConfigInput, "title"> & { eventName: AnalyticsEvent; surveyId: string; surveyName: string }) {
  const { capture } = useAnalytics()
  useEffect(() => {
    capture(eventName, { survey_id: surveyId, survey_name: surveyName })
  }, [capture, eventName, surveyId, surveyName])
  const theme = useTheme()

  return (
    <Flex align="center" justify="flex-start" style={{ paddingBottom: theme.spacing.xs }}>
      <Row>
        <Heading3>
          {title}
          <Tag color="warning" style={{ marginLeft: theme.spacing.xxs }}>
            BETA
          </Tag>
        </Heading3>
      </Row>
    </Flex>
  )
}

type ContentProps = {
  onSubmit: () => void
}

export function Content({ onSubmit }: ContentProps) {
  const { capture } = useAnalytics()
  const theme = useTheme()
  const [form] = Form.useForm()

  const handleSubmit = (values: { rating: string; feedback: string }) => {
    const payload = {
      survey_name: EXPECT_AI_SURVEY_TEXT.surveyName,
      survey_id: EXPECT_AI_SURVEY_TEXT.surveyId,
      rating: values.rating,
      feedback: values.feedback,
    }
    capture("survey.submitted", payload)
    onSubmit()
  }

  const options = [
    { value: "not_helpful", label: "Not helpful at all", icon: <Icon name="frown" /> },
    { value: "somewhat_helpful", label: "Somewhat helpful", icon: <Icon name="smile" /> },
    { value: "very_helpful", label: "Exactly what I needed", icon: <Icon name="smileBeam" /> },
  ]

  return (
    <Form form={form} onFinish={handleSubmit}>
      <Flex vertical gap={theme.spacing.m}>
        <BodyNormal>Did you find the recommended Expectations helpful?</BodyNormal>
        <Form.Item name="rating" rules={[{ required: true, message: "Please select an option" }]}>
          <Radio.Group>
            <Space direction="vertical">
              {options.map((option) => (
                <Radio key={option.value} value={option.value}>
                  <Space>
                    {option.icon}
                    {option.label}
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="feedback">
          <Input.TextArea placeholder="Additional feedback to improve ExpectAI (optional)" rows={4} />
        </Form.Item>

        <Flex justify="flex-end">
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Flex>
      </Flex>
    </Form>
  )
}
